import { track, EVENTS } from '../analytics';
import { readSSE } from './sseFetch';

// No provider message, transcript, consultation identifier or token leaves this
// helper. Request IDs are LOCAL operation IDs, not server trace IDs.
const knownCodes = new Set(['timeout', 'invalid_request', 'busy', 'rate_limited', 'unauthorized', 'service_error', 'network_error', 'invalid_stream', 'incomplete_stream', 'empty_result', 'short_result', 'cancelled', 'save_failed']);
export function refinementError(code, stage) {
  const error = new Error(code);
  error.refineCode = knownCodes.has(code) ? code : 'service_error';
  error.refineStage = stage;
  return error;
}
export function refinementMessage(error) {
  const code = error?.refineCode;
  if (code === 'timeout') return 'Refinement took too long. Please try again. Original version kept.';
  if (code === 'unauthorized') return 'The refinement service could not authorize this request. Please contact support. Original version kept.';
  if (code === 'invalid_request') return 'The refinement service could not process this transcript. Please contact support. Original version kept.';
  if (code === 'empty_result') return 'AI returned no text. Please try again. Original version kept.';
  if (code === 'invalid_stream') return 'The AI response could not be read. Please try again. Original version kept.';
  if (code === 'busy') return 'Refine service busy — please try again. Original version kept.';
  if (code === 'rate_limited') return 'Refine service limit reached — please try again later. Original version kept.';
  if (code === 'short_result') return "Refined text came back much shorter than the original, so it wasn't applied. Original version kept — please try again.";
  if (code === 'network_error' || code === 'incomplete_stream') return 'Refinement connection interrupted — please try again. Original version kept.';
  return 'Could not refine transcript — please try again. Original version kept.';
}
export const canRetryRefinement = error => !['unauthorized', 'invalid_request', 'cancelled'].includes(error?.refineCode);
export function refinementRetryAt(value, now = Date.now()) {
  if (!value?.trim()) return null;
  const seconds = Number(value);
  const time = Number.isFinite(seconds) ? now + Math.max(0, seconds) * 1000 : Date.parse(value);
  return Number.isFinite(time) && time > now ? time : null;
}
const statusCode = status => [408, 504].includes(status) ? 'timeout' : [400, 413, 422].includes(status) ? 'invalid_request' : status === 503 ? 'busy' : status === 429 ? 'rate_limited' : [401, 403].includes(status) ? 'unauthorized' : 'service_error';
function providerCode(payload) {
  // Inspect only to classify; never retain or emit the provider's raw payload.
  if (typeof payload !== 'string') return 'service_error';
  if (/\b408\b|\b504\b|\bDEADLINE_EXCEEDED\b/i.test(payload)) return 'timeout';
  if (/\b401\b|\b403\b|\bUNAUTHENTICATED\b|\bPERMISSION_DENIED\b/i.test(payload)) return 'unauthorized';
  if (/\b400\b|\b413\b|\b422\b|\bINVALID_ARGUMENT\b/i.test(payload)) return 'invalid_request';
  if (/\b503\b|\bUNAVAILABLE\b|\boverloaded\b/i.test(payload)) return 'busy';
  if (/\b429\b|\bRESOURCE_EXHAUSTED\b/i.test(payload)) return 'rate_limited';
  return 'service_error';
}
export function safeRefinementSaveError(error) {
  return {
    name: 'Error', message: 'Refined transcript could not be saved',
    status: Number.isInteger(error?.status) && error.status >= 100 && error.status <= 599 ? error.status : null,
    code: /^(?:[0-9]{5}|PGRST[0-9]{3})$/.test(error?.code || '') ? error.code : null,
  };
}
export function createRefinementDiagnostics(entry, text) {
  const start = Date.now();
  const info = {
    request_id: (typeof window !== 'undefined' && window.crypto?.randomUUID?.()) || `refine-${start}-${Math.random().toString(36).slice(2)}`,
    entry: entry === 'write' ? 'write' : 'live',
    input_chars: text.length,
    input_bytes: new Blob([text]).size,
    output_chars: 0, chunk_count: 0, done_received: false,
  };
  const emit = (event, extra = {}) => {
    try {
      const record = { ...info, ...extra, elapsed_ms: Date.now() - start,
        online: typeof navigator === 'undefined' ? null : navigator.onLine,
        visibility: typeof document === 'undefined' ? null : document.visibilityState };
      console.info('[refine]', event, record);
      // Existing ingestion allowlist: detailed counters stay in console. No
      // new server schema is needed; reason is a generated local request ID.
      track(EVENTS.REFINEMENT_DIAGNOSTIC, {
        type: info.entry, stage: event, reason: info.request_id,
        duration_ms: record.elapsed_ms, count: info.input_bytes,
        error_code: extra.error_code || null,
      });
    } catch (_) { /* diagnostics must never break refinement */ }
  };
  emit('started');
  return {
    info, emit, elapsed: () => Date.now() - start,
    failure(error, cancelled = false) {
      emit(cancelled ? 'cancelled' : 'failed', {
        failure_stage: error?.refineStage || 'stream',
        error_code: cancelled ? 'cancelled' : knownCodes.has(error?.refineCode) ? error.refineCode : 'network_error',
      });
    },
    saveFailed(error) {
      const { status, code } = safeRefinementSaveError(error);
      emit('save_failed', { failure_stage: 'persistence', error_code: code || 'save_failed', http_status: status });
    },
  };
}
// Refinement only adds speaker roles, so the result is normally as long as the
// original or longer. A model that stops early can still send [DONE]; a much
// shorter result would silently drop most of a long session if accepted.
// Short inputs are skipped: there, relabelling alone can change the length a lot.
export const SHORT_RESULT_MIN_INPUT_CHARS = 2000;
export const SHORT_RESULT_MIN_RATIO = 0.7;
export function isSuspiciouslyShort(input, output) {
  const original = (input || '').trim().length;
  return original >= SHORT_RESULT_MIN_INPUT_CHARS && (output || '').trim().length < original * SHORT_RESULT_MIN_RATIO;
}
export async function refineTranscript({ transcript, signal, onPreview, diagnostics }) {
  const diag = diagnostics;
  let stage = 'request';
  try {
    const response = await fetch(process.env.REACT_APP_STREAM_BACKEND_URL, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ transcript }), signal,
    });
    diag.info.response_ms = diag.elapsed();
    diag.emit('response_received', { http_status: response.status ?? null });
    if (!response.ok) {
      const error = refinementError(statusCode(response.status), 'response');
      error.retryAt = refinementRetryAt(response.headers?.get?.('Retry-After'));
      throw error;
    }
    stage = 'stream';
    let accumulated = '';
    for await (const { event, data } of readSSE(response)) {
      if (signal?.aborted) throw refinementError('cancelled', stage);
      if (data === '[DONE]' && event !== 'error') { diag.info.done_received = true; break; }
      if (!data && event !== 'error') continue;
      let value;
      try { value = JSON.parse(data); } catch (_) { throw refinementError('invalid_stream', 'parsing'); }
      if (event === 'error') throw refinementError(providerCode(value), 'provider');
      if (typeof value !== 'string') throw refinementError('invalid_stream', 'parsing');
      if (!value) continue;
      accumulated += value;
      diag.info.output_chars = accumulated.length;
      diag.info.chunk_count += 1;
      diag.info.last_chunk_ms = diag.elapsed();
      if (diag.info.chunk_count === 1) {
        diag.info.first_chunk_ms = diag.info.last_chunk_ms;
        diag.emit('first_chunk');
      }
      onPreview?.(accumulated);
    }
    if (signal?.aborted) throw refinementError('cancelled', stage);
    if (!diag.info.done_received) throw refinementError('incomplete_stream', 'completion');
    if (!accumulated.trim()) throw refinementError('empty_result', 'completion');
    if (isSuspiciouslyShort(transcript, accumulated)) throw refinementError('short_result', 'completion');
    diag.emit('completed');
    return accumulated;
  } catch (error) {
    const safe = error?.refineCode ? error : refinementError('network_error', stage);
    diag.failure(safe, Boolean(signal?.aborted));
    throw safe;
  }
}
